/**
 * Build-time identity exposed in the sidebar footer.
 *
 * Read by the canonical App Shell (per the sustevo-handbook
 * `brand/APP_SHELL.md` § 7 "Sidebar footer" — version + API host
 * format is shared across every SustEvo SaaS so the footer reads
 * identically in side-by-side comparisons).
 *
 * Format: `v<semver> · 0x<sha7>` and `<api-host>` (host-only, no
 * protocol / no path). See APP_SHELL.md § 7 for the canonical shape.
 *
 * Env source:
 *   - `VITE_APP_VERSION` — semver string. Falls back to `0.0.0` when
 *     unset (dev builds without a version pin).
 *   - `VITE_GIT_SHA`     — full or short commit sha. Falls back to
 *     `dev` when unset.
 *   - `VITE_CORE_API_BASE_URL` — Senviro reads this elsewhere as
 *     `VITE_API_BASE_URL`; both are honoured to stay consistent with
 *     the cross-app convention while not breaking the existing
 *     deployment env-var name.
 *
 * Static reads only (no `const env = import.meta.env` aliasing) so
 * Vite's `define` transform replaces them at build time, per
 * `apps/web/src/observability/sentry.ts` header note.
 */

function readEnv(key: string): string | undefined {
  try {
    // Static property accesses so Vite's transform can replace them.
    // Each branch reads the literal key, not via an index expression.
    switch (key) {
      case 'VITE_APP_VERSION':
        return import.meta.env.VITE_APP_VERSION;
      case 'VITE_GIT_SHA':
        return import.meta.env.VITE_GIT_SHA;
      case 'VITE_CORE_API_BASE_URL':
        return import.meta.env.VITE_CORE_API_BASE_URL;
      case 'VITE_API_BASE_URL':
        return import.meta.env.VITE_API_BASE_URL;
      default:
        return undefined;
    }
  } catch {
    return undefined;
  }
}

export interface BuildVersion {
  /** `v<semver> · 0x<sha7>` per APP_SHELL.md § 7. */
  version: string;
  /** Host portion of the API base URL (no protocol, no path). */
  apiHost: string;
}

export function getBuildVersion(): BuildVersion {
  // `||` (not `??`) — vitest's `vi.stubEnv('KEY', '')` sets the env value to
  // empty string, not undefined. We want empty strings to fall through to
  // the fallback, same as missing keys.
  const rawVersion = readEnv('VITE_APP_VERSION') || '0.0.0';
  const rawSha = readEnv('VITE_GIT_SHA');
  const sha7 =
    typeof rawSha === 'string' && rawSha.length >= 7
      ? rawSha.slice(0, 7)
      : 'dev';
  const version = `v${rawVersion} · 0x${sha7}`;

  const rawApi =
    readEnv('VITE_CORE_API_BASE_URL') ||
    readEnv('VITE_API_BASE_URL') ||
    '';
  let apiHost = rawApi;
  try {
    if (rawApi) apiHost = new URL(rawApi).host;
  } catch {
    // Leave the raw value if it isn't a parseable URL — better than
    // showing nothing when the env-var contains an unexpected format.
  }
  if (!apiHost) apiHost = 'unknown';

  return { version, apiHost };
}
